import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Pencil, Trash2, CheckSquare, Calendar, User, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import TaskForm from "@/components/tasks/TaskForm";
import TimeTracker from "@/components/time/TimeTracker";
import CommentSection from "@/components/collaboration/CommentSection";

const statusColors = {
  todo: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

const statusLabels = {
  todo: "Å Gjøre",
  in_progress: "Pågår",
  completed: "Fullført",
  cancelled: "Kansellert",
};

const priorityColors = {
  low: "bg-blue-100 text-blue-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-rose-100 text-rose-700",
};

const priorityLabels = {
  low: "Lav",
  medium: "Middels",
  high: "Høy",
  urgent: "Haster",
};

export default function TaskDetail() {
  const [taskId, setTaskId] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    setTaskId(id);
  }, []);

  const { data: task } = useQuery({
    queryKey: ["task", taskId],
    queryFn: async () => {
      const tasks = await base44.entities.Task.filter({ id: taskId });
      return tasks[0];
    },
    enabled: !!taskId,
  });

  const { data: timeEntries = [] } = useQuery({
    queryKey: ["task-time-entries", taskId],
    queryFn: () => base44.entities.TimeEntry.filter({ task_id: taskId }, "-start_time"),
    enabled: !!taskId,
  });

  const { data: subtasks = [] } = useQuery({
    queryKey: ["subtasks", taskId],
    queryFn: () => base44.entities.Task.filter({ parent_task_id: taskId }),
    enabled: !!taskId,
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Task.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setShowTaskForm(false);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id) => base44.entities.Task.delete(id),
    onSuccess: () => {
      navigate(createPageUrl("Tasks"));
    },
  });

  const totalMinutes = timeEntries
    .filter(e => !e.is_running && e.duration_minutes)
    .reduce((sum, e) => sum + e.duration_minutes, 0);
  const totalHours = (totalMinutes / 60).toFixed(1);

  const checklistProgress = task?.checklist 
    ? (task.checklist.filter(item => item.completed).length / task.checklist.length) * 100 
    : 0;

  const subtasksProgress = subtasks.length > 0
    ? (subtasks.filter(st => st.status === "completed").length / subtasks.length) * 100
    : 0;

  if (!task) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(createPageUrl("Tasks"))}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Tilbake til Oppgaver
        </Button>

        {/* Task Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <CheckSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-900 mb-2">{task.title}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className={cn("border-0", statusColors[task.status])}>
                    {statusLabels[task.status]}
                  </Badge>
                  <Badge className={cn("border-0", priorityColors[task.priority])}>
                    {priorityLabels[task.priority]}
                  </Badge>
                  {task.assigned_to_name && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <User className="w-4 h-4" />
                      <span>{task.assigned_to_name}</span>
                    </div>
                  )}
                  {task.due_date && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Calendar className="w-4 h-4" />
                      <span>{format(new Date(task.due_date), "dd.MM.yyyy")}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => setShowTaskForm(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="text-rose-600 hover:text-rose-700"
                onClick={() => {
                  if (confirm("Er du sikker på at du vil slette denne oppgaven?")) {
                    deleteTaskMutation.mutate(taskId);
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {task.description && (
            <p className="text-slate-600 mb-4">{task.description}</p>
          )}

          {task.project_name && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="font-medium">Prosjekt:</span>
              <span>{task.project_name}</span>
            </div>
          )}

          {/* Time Spent */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-zinc-900" />
              <span className="text-sm font-medium text-slate-700">Tid brukt:</span>
              <span className="text-sm font-bold text-zinc-900">{totalHours}t</span>
              <span className="text-xs text-slate-500">({timeEntries.length} registreringer)</span>
            </div>
          </div>
        </div>

        {/* Progress Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {task.checklist && task.checklist.length > 0 && (
            <Card className="p-6 bg-white border-0 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Sjekkliste</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                  <span>{task.checklist.filter(item => item.completed).length} av {task.checklist.length} fullført</span>
                  <span className="font-medium">{Math.round(checklistProgress)}%</span>
                </div>
                <Progress value={checklistProgress} className="h-2" />
              </div>
            </Card>
          )}

          {subtasks.length > 0 && (
            <Card className="p-6 bg-white border-0 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Deloppgaver</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                  <span>{subtasks.filter(st => st.status === "completed").length} av {subtasks.length} fullført</span>
                  <span className="font-medium">{Math.round(subtasksProgress)}%</span>
                </div>
                <Progress value={subtasksProgress} className="h-2" />
              </div>
            </Card>
          )}
        </div>

        {/* Time Tracker */}
        <div className="mb-6">
          <TimeTracker 
            taskId={taskId}
            taskTitle={task.title}
            projectId={task.project_id}
            projectName={task.project_name}
            relatedToType={task.related_to_type}
            relatedToId={task.related_to_id}
            relatedToName={task.related_to_name}
          />
        </div>

        {/* Time Entries History */}
        {timeEntries.length > 0 && (
          <Card className="p-6 bg-white border-0 shadow-sm mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">Tidsregistreringer</h3>
            <div className="space-y-3">
              {timeEntries.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{entry.user_name}</p>
                    <p className="text-xs text-slate-500">
                      {format(new Date(entry.start_time), "dd.MM.yyyy HH:mm")}
                    </p>
                    {entry.description && (
                      <p className="text-sm text-slate-600 mt-1">{entry.description}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-900">
                      {((entry.duration_minutes || 0) / 60).toFixed(1)}t
                    </p>
                    {entry.billable && (
                      <span className="text-xs text-emerald-600">Fakturerbar</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Comments */}
        <CommentSection 
          relatedToType="task"
          relatedToId={taskId}
          relatedToName={task.title}
          projectId={task.project_id}
          projectName={task.project_name}
        />
      </div>

      {/* Task Form */}
      {showTaskForm && (
        <TaskForm
          task={task}
          onSubmit={(data) => updateTaskMutation.mutate({ id: taskId, data })}
          onClose={() => setShowTaskForm(false)}
          isLoading={updateTaskMutation.isPending}
        />
      )}
    </div>
  );
}